import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Megaphone, Star, ExternalLink, ChevronRight, CheckCircle2,
  Target, Rocket, DollarSign, BarChart3, Users, Globe, ShieldCheck, PlayCircle,
  Camera, Circle, Hash,
} from "lucide-react";
import { FaTiktok, FaPodcast, FaEnvelopeOpenText, FaUserGroup, FaPatreon, FaVimeoV } from "react-icons/fa6";
import { Layout } from "../components/Layout";
import { BuilderLogo } from "../components/BuilderLogo";
import { PreviewThumb } from "../components/PreviewThumb";
import { resourcesLibrary, resourcesFavorites, resourcesToggleFavorite } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const CHIPS = ["All", "TikTok", "Instagram", "YouTube", "UGC", "Podcasts", "Newsletters", "B2B", "Ecommerce"];
const FEATURED_ID = "aspire";

const POPULAR = [
  { label: "TikTok Creators", icon: FaTiktok, color: "#010101", url: "https://www.tiktok.com/@its_joes_pov" },
  { label: "UGC Creators", icon: FaUserGroup, color: "#8a6d1a", url: "https://www.tiktok.com/tag/ugccreator" },
  { label: "Podcast Promotion", icon: FaPodcast, color: "#8E44AD", url: "https://podcorn.com" },
  { label: "Newsletter Sponsorships", icon: FaEnvelopeOpenText, color: "#2563EB", url: "https://www.paved.com" },
  { label: "Patreon", icon: FaPatreon, color: "#FF424D", url: "https://www.patreon.com" },
  { label: "Circle", icon: Circle, color: "#4F46E5", url: "https://circle.so" },
  { label: "Vimeo", icon: FaVimeoV, color: "#1AB7EA", url: "https://vimeo.com" },
  { label: "Soona", icon: Camera, color: "#111827", url: "https://soona.co" },
  { label: "Hashtag Paid", icon: Hash, color: "#F5576C", url: "https://www.hashtagpaid.com" },
];

const WHY = [
  { icon: Target, title: "Reach Your Audience", text: "Connect with creators who already have your target audience's attention." },
  { icon: Rocket, title: "Grow Faster", text: "Accelerate growth with authentic content and trusted recommendations." },
  { icon: DollarSign, title: "Cost Effective", text: "Find creators that match your budget and campaign goals." },
  { icon: BarChart3, title: "Track Results", text: "Measure performance and optimize campaigns for better ROI." },
];

export default function InfluencerMarketing() {
  const { user, requireAuth } = useAuth();
  const [tools, setTools] = useState([]);
  const [query, setQuery] = useState("");
  const [chip, setChip] = useState("All");
  const [favIds, setFavIds] = useState([]);
  const whyRef = useRef(null);

  useEffect(() => {
    resourcesLibrary().then((d) => setTools((d.tools || []).filter((t) => t.category === "influencer-marketing"))).catch(() => {});
    window.scrollTo(0, 0);
  }, []);
  const loadFavs = useCallback(() => { if (user) resourcesFavorites().then((d) => setFavIds(d.ids || [])).catch(() => {}); else setFavIds([]); }, [user]);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  const featured = useMemo(() => tools.find((t) => t.id === FEATURED_ID), [tools]);

  const shown = useMemo(() => {
    let list = tools;
    if (chip !== "All") list = list.filter((t) => (t.tags || []).some((x) => x.toLowerCase() === chip.toLowerCase()) || (t.best_for || "").toLowerCase().includes(chip.toLowerCase()));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((t) => t.name.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q) || (t.kind || "").toLowerCase().includes(q) || (t.tags || []).some((x) => x.toLowerCase().includes(q)));
    }
    return list;
  }, [tools, chip, query]);

  const toggleFav = (id) => {
    const on = !favIds.includes(id);
    setFavIds((p) => on ? [...p, id] : p.filter((x) => x !== id));
    const run = async () => { try { const d = await resourcesToggleFavorite(id, on); setFavIds(d.ids || []); if (on) toast.success("Saved to your favorites"); } catch { loadFavs(); } };
    user ? run() : requireAuth(run);
  };

  const Card = ({ t }) => (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 hover:shadow-[0_8px_24px_rgb(0,0,0,0.06)] transition-shadow flex flex-col" data-testid={`inf-card-${t.id}`}>
      <PreviewThumb url={t.url} name={t.name} />
      <div className="flex items-start justify-between gap-2">
        <BuilderLogo url={t.url} name={t.name} size={44} />
        <button onClick={() => toggleFav(t.id)} className="text-[#9aa1ab] hover:text-[#D4AF37]" data-testid={`inf-fav-${t.id}`} aria-label="Save">
          <Star size={18} className={favIds.includes(t.id) ? "text-[#D4AF37] fill-[#D4AF37]" : ""} />
        </button>
      </div>
      <p className="font-display font-bold text-[#0F0F0F] mt-3">{t.name}</p>
      {t.kind && <p className="text-xs font-semibold text-[#B8901F] mt-0.5">{t.kind}</p>}
      <p className="text-sm text-[#687280] mt-2 flex-1">{t.desc}</p>
      {t.best_for && (
        <p className="text-xs text-[#374151] mt-3"><span className="font-bold text-[#0F0F0F]">Best For:</span> {t.best_for}</p>
      )}
      {t.tags && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {t.tags.map((x) => <span key={x} className="text-[10px] font-semibold text-[#8a6d1a] bg-[#FBF7EA] rounded-full px-2 py-0.5">{x}</span>)}
        </div>
      )}
      <div className="flex items-center gap-2 mt-4">
        <a href={t.url} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#0F0F0F] text-white rounded-full py-2.5 text-xs font-bold hover:bg-[#1c1c1c]" data-testid={`inf-visit-${t.id}`}>Visit Website <ExternalLink size={12} /></a>
        <button onClick={() => toggleFav(t.id)} className={`inline-flex items-center justify-center gap-1.5 rounded-full py-2.5 px-4 text-xs font-bold border transition-colors ${favIds.includes(t.id) ? "bg-[#FBF7EA] border-[#D4AF37] text-[#8a6d1a]" : "bg-white border-gray-200 text-[#374151] hover:border-[#D4AF37]"}`} data-testid={`inf-save-${t.id}`}>
          <Star size={13} className={favIds.includes(t.id) ? "fill-[#D4AF37] text-[#D4AF37]" : ""} /> {favIds.includes(t.id) ? "Saved" : "Save"}
        </button>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="influencer-marketing-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-[#687280] mb-6">
          <Link to="/" className="hover:text-[#0F0F0F]">Founder Resource Library</Link>
          <ChevronRight size={14} />
          <span className="text-[#0F0F0F] font-semibold">Influencer Marketing</span>
        </div>

        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-[#FBF7EA] to-white border border-[#D4AF37]/20 p-8 sm:p-10 relative overflow-hidden" data-testid="inf-hero">
          <div className="max-w-2xl relative z-10">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F0F0F] flex items-center gap-2">Influencer Marketing <Megaphone className="text-[#D4AF37]" size={28} /></h1>
            <p className="text-[#687280] mt-3 text-base sm:text-lg">Discover trusted influencer marketing platforms to promote your startup, app, or business.</p>
            <button onClick={() => whyRef.current?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 mt-6 bg-[#0F0F0F] text-white rounded-full px-5 py-3 text-sm font-bold hover:bg-[#1c1c1c]" data-testid="inf-how-btn"><PlayCircle size={16} /> Learn How It Works</button>
          </div>
          <Megaphone size={220} className="absolute -right-6 -bottom-10 text-[#D4AF37]/10" />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="inf-stats">
          {[
            { icon: Users, big: `${tools.length}+`, top: "Top Platforms", sub: "Curated & Verified" },
            { icon: Globe, big: "8", top: "Channels", sub: "All marketing channels" },
            { icon: Star, big: "1000+", top: "Creators Access", sub: "Through these platforms" },
            { icon: ShieldCheck, big: "Trusted", top: "Safe & Secure", sub: "Verified platforms" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-[#FBF7EA] text-[#B8901F] flex items-center justify-center shrink-0"><s.icon size={18} /></span>
              <div><p className="font-display text-lg font-extrabold text-[#0F0F0F] leading-none">{s.big}</p><p className="text-xs font-semibold text-[#374151] mt-1">{s.top}</p><p className="text-[10px] text-[#9aa1ab]">{s.sub}</p></div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-8 mt-8">
          {/* Main */}
          <div>
            {/* Search */}
            <div className="relative">
              <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#9aa1ab]" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search influencer marketing platforms..." className="w-full rounded-full border border-gray-200 pl-12 pr-5 py-3.5 text-sm focus:outline-none focus:border-[#D4AF37] shadow-[0_4px_20px_rgb(0,0,0,0.04)]" data-testid="inf-search" />
            </div>

            {/* Chips */}
            <div className="flex flex-wrap gap-2 mt-5" data-testid="inf-chips">
              {CHIPS.map((c) => (
                <button key={c} onClick={() => setChip(c)} className={`rounded-full px-3.5 py-2 text-xs font-semibold border transition-colors ${chip === c ? "bg-[#0F0F0F] text-white border-[#0F0F0F]" : "bg-white text-[#374151] border-gray-200 hover:border-[#D4AF37]"}`} data-testid={`inf-chip-${c.toLowerCase()}`}>{c}</button>
              ))}
            </div>

            <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-8 mb-4">Top Influencer Marketing Platforms</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="inf-cards">
              {shown.map((t) => <Card key={t.id} t={t} />)}
              {shown.length === 0 && <p className="col-span-full text-center text-sm text-[#9aa1ab] py-8">No platforms match your filters.</p>}
            </div>
          </div>

          {/* Right rail */}
          <aside className="space-y-6">
            {featured && (
              <div className="rounded-3xl border border-[#D4AF37]/30 bg-white p-6 shadow-[0_8px_28px_rgb(212,175,55,0.12)]" data-testid="inf-featured">
                <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#B8901F]"><Star size={13} className="fill-[#D4AF37] text-[#D4AF37]" /> Featured Platform</p>
                <div className="flex flex-col items-center text-center mt-4">
                  <BuilderLogo url={featured.url} name={featured.name} size={56} />
                  <p className="font-display text-2xl font-extrabold text-[#0F0F0F] mt-3">{featured.name}</p>
                  <span className="mt-2 text-[11px] font-bold text-[#8a6d1a] bg-[#FBF7EA] rounded-full px-3 py-1">Top Choice</span>
                  <p className="text-sm text-[#687280] mt-3">Recommended for founders looking to collaborate with creators.</p>
                </div>
                <ul className="space-y-2 mt-5">
                  {["Affordable pricing", "Easy to use", "Huge creator network", "All major platforms"].map((x) => (
                    <li key={x} className="flex items-center gap-2 text-sm text-[#374151]"><CheckCircle2 size={15} className="text-[#D4AF37]" /> {x}</li>
                  ))}
                </ul>
                <a href={featured.url} target="_blank" rel="noopener noreferrer" className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3 text-sm font-bold hover:bg-[#c5a030]" data-testid="inf-featured-visit">Visit {featured.name} <ExternalLink size={14} /></a>
              </div>
            )}

            {/* Popular Categories — real social platforms */}
            <div className="rounded-3xl border border-gray-100 bg-white p-5" data-testid="inf-popular">
              <p className="font-display font-bold text-[#0F0F0F] mb-3">Popular Categories</p>
              <div className="space-y-1">
                {POPULAR.map((p) => (
                  <a key={p.label} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[#FBF7EA] transition-colors group" data-testid={`inf-popular-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: p.color }}><p.icon size={15} /></span>
                    <span className="text-sm font-semibold text-[#374151] flex-1 group-hover:text-[#0F0F0F]">{p.label}</span>
                    <ChevronRight size={15} className="text-[#c7cbd1] group-hover:text-[#B8901F]" />
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-dashed border-[#D4AF37]/50 bg-[#FBF7EA]/40 p-5 text-center">
              <p className="font-display font-bold text-[#0F0F0F]">Only trusted platforms</p>
              <p className="text-sm text-[#687280] mt-1">We only recommend trusted platforms. All links are unbiased.</p>
            </div>
          </aside>
        </div>

        {/* Why use */}
        <div ref={whyRef} className="mt-14 rounded-3xl bg-[#0F0F0F] text-white p-8 sm:p-10" data-testid="inf-why">
          <h2 className="font-display text-2xl font-extrabold">Why Use Influencer Marketing Platforms?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {WHY.map((w) => (
              <div key={w.title}>
                <span className="w-11 h-11 rounded-2xl bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center"><w.icon size={20} /></span>
                <p className="font-display font-bold mt-3">{w.title}</p>
                <p className="text-sm text-white/60 mt-1.5">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
